import { getRenderInfoBuffer } from 'src/lib/player/RenderInfoManager';
import { triggerReload } from 'src/lib/player/model';
import {
  getCurrentAvailableStarterGroups,
  getCurrentAvailableTags,
  getCurrentSelectedIds,
  getHiddenIds,
} from 'src/lib/player/selectors';
import {
  type PoiType,
  type StarterType,
  type State,
  type TrackType,
  useStore,
} from 'src/lib/player/store';
import { memoizeSelector } from 'src/lib/player/utils';
import { PlayerSidebar } from '../PlayerSidebar';

const getCurrentStartersAndPois = memoizeSelector(
  (state: State): [Array<StarterType>, Array<TrackType>, Array<PoiType>] => [
    state.currentEventIndices.map((i) => state.events[i]).flatMap((event) => event.starters),
    state.currentEventIndices.map((i) => state.events[i]).flatMap((event) => event.tracks),
    state.currentEventIndices
      .map((i) => state.events[i])
      .flatMap((event) => event.pois)
      .sort((a, b) => (a.offset != null ? (b.offset != null ? a.offset - b.offset : -1) : 1)),
  ],
  (state: State) => state.currentEventIndices.map((i) => state.events[i]),
);

type Props = {
  onClose: () => void;
};

export default function ConnectedPlayerSidebar({ onClose }: Props): JSX.Element {
  const setSelected = useStore((state) => state.actions.setSelected);
  const setSelectedTags = useStore((state) => state.actions.setSelectedTags);
  const hide = useStore((state) => state.actions.hide);
  const unhide = useStore((state) => state.actions.unhide);

  const [allCurrentStarters, allCurrentTracks, allCurrentPois] =
    useStore(getCurrentStartersAndPois);
  const currentEventIndices = useStore((state) => state.currentEventIndices);
  const allEvents = useStore((state) => state.events);
  const eventType = useStore((state) => state.options.type);
  const currentSelectedIds = useStore(getCurrentSelectedIds);
  const hiddenIds = useStore((state) => getHiddenIds(state.timer));
  const selectedTags = useStore((state) => state.timer.selectedTags);
  const defaultSelectedTags = useStore((state) => state.timer.defaultSelectedTags);
  const availableTags = useStore(getCurrentAvailableTags);
  const availableStarterGroups = useStore(getCurrentAvailableStarterGroups);
  const renderInfoBuffer = useStore(getRenderInfoBuffer);

  const reloadInterval = useStore((state) => state.events.at(0)?.options.reloadInterval ?? 0);
  const eventId = useStore((state) => state.eventId);
  const isReloading = useStore((state) => state.isReloading);
  const autoReloadEnabled = reloadInterval > 0;

  async function handleReload() {
    await triggerReload(eventId);
  }

  return (
    <PlayerSidebar
      onClose={onClose}
      allEvents={allEvents}
      currentEventIndices={currentEventIndices}
      eventType={eventType}
      allCurrentStarters={allCurrentStarters}
      allCurrentTracks={allCurrentTracks}
      allCurrentPois={allCurrentPois}
      currentSelectedIds={currentSelectedIds}
      defaultSelectedTags={defaultSelectedTags}
      hiddenIds={hiddenIds}
      selectedTags={selectedTags}
      availableTags={availableTags}
      availableStarterGroups={availableStarterGroups}
      renderInfoBuffer={renderInfoBuffer}
      onSetSelected={setSelected}
      onSetSelectedTags={setSelectedTags}
      onHide={hide}
      onUnhide={unhide}
      autoReloadEnabled={autoReloadEnabled}
      isReloading={isReloading}
      onReload={handleReload}
    />
  );
}
